import React, { memo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import fontService, { FontWeight, TextType } from 'services/FontService';
import { desktop, tablet } from 'services/ui';
import { Colors } from 'context';
import { GroupServiceInfo } from 'molecules/GroupServiceInfo';
import { GroupServiceInfo as IGroupService } from '../../../config/groupOverrides';
import { CourseStatus } from './CourseStatus';

export interface ICourseCard {
  id: string;
  courseId: string;
  courseName: string;
  groupName: string;
  studentsCount: number;
  courseImageURL: string;
  activeLessonNumber: number;
  lessonsCount: number;
  nextLesson: string;
  educationForm: string;
  statusFinished: boolean;
  /**
   * Группа «без расписания»: прячем количество студентов, счётчик занятий и
   * дату ближайшего занятия. Считается на стороне страницы — режим включается
   * только студенту.
   */
  hideSchedule?: boolean;
  /** Реквизиты услуги (BUR); показываются всем ролям. */
  service?: IGroupService | null;
}

export const CourseCard = memo(
  ({
    courseName,
    groupName,
    studentsCount,
    activeLessonNumber,
    courseImageURL,
    lessonsCount,
    nextLesson,
    statusFinished,
    educationForm,
    hideSchedule = false,
    service = null,
  }: ICourseCard) => {
    const { t } = useTranslation();
    return (
      <Wrapper $hasService={Boolean(service)}>
        <CourseStatus
          statusFinished={statusFinished}
          educationForm={educationForm}
          isFree={courseName.toLowerCase().includes('free'.toLowerCase())}
        />
        <CourseName>{courseName}</CourseName>
        <ServiceInfoStyled service={service} />
        <InfoContainer>
          <Info>
            {t('courseInfo.group')} <InfoValue>{groupName}</InfoValue>
          </Info>
          {!hideSchedule && (
            <>
              <Info>
                {t('courseInfo.amount')}{' '}
                <InfoValue>
                  {studentsCount} {t('courseInfo.people')}
                </InfoValue>
              </Info>
              <Info>
                {t('courseInfo.lessons')}{' '}
                <InfoValue>
                  {activeLessonNumber}/{lessonsCount}
                </InfoValue>
              </Info>
              {!statusFinished && (
                <InfoNextLesson>
                  {t('courseInfo.nextLesson')}
                  <InfoValue>{nextLesson}</InfoValue>
                </InfoNextLesson>
              )}
            </>
          )}
        </InfoContainer>
        <CourseImage src={courseImageURL} alt={t('courseInfo.courseImageAlt')} />
      </Wrapper>
    );
  },
);

// Высота карточки на tablet/desktop зафиксирована, а `InfoContainer` там
// прибит к низу абсолютом — два дополнительных поля реквизитов в неё просто не
// влезают и обрезаются `overflow: hidden`. Поэтому карточка с реквизитами выше.
const Wrapper = styled.div<{ $hasService: boolean }>`
  padding: 24px 20px 128px;
  border-radius: 16px;
  box-sizing: border-box;
  background-color: ${Colors.white};
  border: 1px solid ${Colors.system_gray};
  cursor: pointer;
  position: relative;
  overflow: hidden;
  &:hover {
    z-index: 10;
    transition: 0.5s;
    filter: drop-shadow(0px 40px 80px rgba(192, 198, 203, 0.8));
  }

  ${tablet} {
    height: ${({ $hasService }) => ($hasService ? '336px' : '240px')};
    padding: 32px 32px;
    border-radius: 24px;
  }

  ${desktop} {
    width: 1110px;
    height: ${({ $hasService }) => ($hasService ? '409px' : '313px')};
    padding: 40px 0 47px 48px;
  }
`;

const ServiceInfoStyled = styled(GroupServiceInfo)`
  margin-bottom: 16px;
  // Изображение курса перекрывает правую часть карточки — держим блок слева.
  max-width: 60%;

  ${desktop} {
    max-width: 500px;
  }
`;

const CourseName = styled.h1`
  ${fontService.h3(133)}
  margin: 12px 0 24px 0;
  max-width: 80%;

  ${tablet} {
    ${fontService.h2(125)}
    margin: 16px 0 24px 0;
    max-width: 466px;
  }

  ${desktop} {
    ${fontService.h1()}
    max-width: 800px;
  }
`;

const InfoContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  position: relative;
  row-gap: 10px;
  z-index: 2;

  ${tablet} {
    flex-wrap: nowrap;
    position: absolute;
    bottom: 32px;
  }

  ${desktop} {
    bottom: 47px;
  }
`;

const Info = styled.div`
  margin-right: 24px;
  ${fontService.text({ type: TextType.extraSmall })};

  &:last-child {
    margin: 0;
  }
`;

const InfoNextLesson = styled(Info)`
  position: absolute;
  bottom: -50px;

  ${tablet} {
    position: static;
  }
`;

const InfoValue = styled.div`
  margin-top: 4px;
  ${fontService.text({ type: TextType.normal, weight: FontWeight.medium })};
`;

const CourseImage = styled.img`
  position: absolute;
  right: -34px;
  bottom: -80px;
  width: 270px;
  height: 210px;
  object-fit: contain;
  object-position: right;

  ${tablet} {
    right: -58px;
    bottom: -112px;
    width: 340px;
    height: 295px;
  }

  ${desktop} {
    bottom: -135px;
    width: 580px;
    height: 380px;
  }
`;
